import { inject, Injectable } from '@angular/core';
import { combineLatest, Observable, ReplaySubject, Subscriber } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { BandwidthSettings, ZpmPrivilege } from '../models/generated/ziroModels';
import { concatMap } from 'rxjs/operators';
import { AuthenticationContext } from './authentication.context';
import { GlobalPropertiesContext } from './global-properties.context';

@Injectable({ providedIn: 'root' })
export class BandwidthSettingsContext {
  private _http = inject(HttpClient);
  private _authenticationContext = inject(AuthenticationContext);
  private _globalPropertiesContext = inject(GlobalPropertiesContext);

  _stateSource = new ReplaySubject<BandwidthSettings>(1);
  state$ = this._stateSource.asObservable();

  private _baseUrl = '/services/microsoft/admin/bandwidth-settings';

  constructor() {
    combineLatest([this._globalPropertiesContext.state$, this._authenticationContext.state$]).subscribe(
      ([globalProperties, currentUser]) => {
        if (
          globalProperties.hostedEnabled &&
          this._authenticationContext.userHasPrivilege(currentUser, ZpmPrivilege.ZIRO_SUPPORT_SETTINGS)
        ) {
          // only call the bandwidth cache if it's needed (ZPM and user is s8support).
          this._init().subscribe();
        } else {
          this._stateSource.next(null);
        }
      },
    );
  }

  get(): Observable<BandwidthSettings> {
    return this._http.get<BandwidthSettings>(this._baseUrl);
  }

  put(settings: BandwidthSettings): Observable<void> {
    return this._http.put<void>(this._baseUrl, settings).pipe(concatMap(() => this._init()));
  }

  delete(): Observable<void> {
    return this._http.delete<void>(this._baseUrl).pipe(concatMap(() => this._init()));
  }

  testConnection(bandwidthSettings: BandwidthSettings): Observable<void> {
    return this._http.post<void>(`${this._baseUrl}/connections`, bandwidthSettings);
  }

  refresh(): Observable<void> {
    return this._init();
  }

  isBandwidthConfigured(bandwidthSettings: BandwidthSettings): boolean {
    return bandwidthSettings && bandwidthSettings.dlrAccountId !== null;
  }

  private _init(): Observable<void> {
    return new Observable<void>((subscriber: Subscriber<void>) => {
      this._get().subscribe((data: BandwidthSettings) => {
        this._stateSource.next(data);
        subscriber.next();
        subscriber.complete();
      });
    });
  }

  private _get(): Observable<BandwidthSettings> {
    return this._http.get<BandwidthSettings>(this._baseUrl);
  }
}
